import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { LogicGroupView } from '@/components/logic/LogicGroupView'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useAnnexRequirements } from '@/features/contract/gating/annexHooks'
import { useDeleteRequirement } from '@/features/contract/gating/hooks'
import type { StageRequirement } from '@/features/contract/gating/types'
import { formatError } from '@/features/admin/stages/components/formatError'
import { AnnexRequirementFormDialog } from './AnnexRequirementFormDialog'
import { AnnexRequirementRow } from './AnnexRequirementRow'

/** Panel admina listujący wymagania publikacji aneksu (DRAFT -> PUBLISHED) per produkt. */
export function AnnexRequirementsPanel({ configurationCode }: { configurationCode: string }) {
  const confirm = useConfirm()
  const reqs = useAnnexRequirements(configurationCode)
  const del = useDeleteRequirement()
  const [creating, setCreating] = useState<{ groupNo: number } | null>(null)

  const { groupNos, byGroupNo, nextGroupNo } = useMemo(() => {
    const byGroupNo = new Map<number, StageRequirement[]>()
    for (const r of reqs.data ?? []) {
      const arr = byGroupNo.get(r.groupNo) ?? []
      arr.push(r)
      byGroupNo.set(r.groupNo, arr)
    }
    const groupNos = [...byGroupNo.keys()].sort((a, b) => a - b)
    const nextGroupNo = (groupNos[groupNos.length - 1] ?? 0) + 1
    return { groupNos, byGroupNo, nextGroupNo }
  }, [reqs.data])

  if (reqs.isLoading) return <p className="text-xs text-slate-400 italic">Ładowanie warunków…</p>

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold">Wymagania publikacji aneksu</h2>
          <p className="text-xs text-slate-500">
            Transition: <code className="rounded bg-slate-100 px-1">DRAFT → PUBLISHED</code>
          </p>
        </div>
        <button
          onClick={() => setCreating({ groupNo: groupNos.length === 0 ? 1 : nextGroupNo })}
          className="text-[11px] bg-sky-600 text-white rounded px-2 py-1"
        >+ Dodaj alternatywę (LUB)</button>
      </div>

      {groupNos.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Brak wymagań - bramka otwarta.</p>
      ) : (
        <LogicGroupView<StageRequirement>
          groups={groupNos.map(gn => ({
            id: gn,
            atoms: byGroupNo.get(gn) ?? [],
          }))}
          renderAtom={r => (
            <AnnexRequirementRow
              req={r}
              onDelete={async () => {
                const ok = await confirm({
                  title: 'Usunąć ten warunek?',
                  description: 'Operacja jest nieodwracalna.',
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(r.id, { onError: e => toast.error(formatError(e)) })
              }}
            />
          )}
          renderGroupFooter={g => (
            <button
              onClick={() => setCreating({ groupNo: Number(g.id) })}
              className="text-[11px] text-emerald-700 hover:text-emerald-900"
            >+ dodaj warunek (I)</button>
          )}
        />
      )}

      {creating && (
        <AnnexRequirementFormDialog
          configurationCode={configurationCode}
          groupNo={creating.groupNo}
          onClose={() => setCreating(null)}
        />
      )}
    </div>
  )
}
